import cn from 'classnames';
import { useContext, forwardRef } from 'react';

import Text from 'components/common/Text';
import { BrandContext } from 'context/BrandContext';
import styles from 'styles/components/common/ToggleButton';

const ToggleButton = forwardRef(
  (
    {
      className = '',
      disabled = false,
      textContainerClassName = '',
      showSelectedAfter = false,
      children,
      handleClick,
      icon: Icon,
      id,
      name,
      role,
      selected,
      type,
      refs,
      setSelected,
      description,
      ...restProps
    },
    ref
  ) => {
    const { brand } = useContext(BrandContext);
    const handleKeyDown = event => {
      if (!refs || !setSelected) return;

      const currentIndex = refs.findIndex(r => r.current === event.currentTarget);
      if (currentIndex === -1) return;

      let newIndex = null;
      const length = refs.length;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        newIndex = (currentIndex + 1) % length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        newIndex = (currentIndex - 1 + length) % length;
      }

      if (newIndex !== null) {
        event.preventDefault();
        setSelected(Number(refs[newIndex].current.id));
        refs[newIndex].current.focus();
      }
    };

    return (
      <button
        id={id}
        name={name}
        disabled={disabled}
        role={role}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(className, 'textMd rounded-2xl', styles[brand], styles[type], {
          [styles.selected]: selected,
          [styles.unselected]: !selected,
        })}
        aria-checked={role === 'checkbox' || role === 'radio' ? selected : undefined}
        aria-pressed={role === 'button' ? selected : undefined}
        aria-selected={role === 'option' ? selected : undefined}
        onMouseUp={event => event.currentTarget.blur()}
        ref={ref}
        {...restProps}
      >
        {Icon ? (
          <div className={cn(textContainerClassName, 'flex w-full items-center')}>
            <div className="flex min-w-0 flex-col text-left">
              {children}
              {description && <Text className="text-left">{description}</Text>}
            </div>
            <div
              className={cn(
                { [styles.showSelectedAfter]: showSelectedAfter },
                'ml-auto flex flex-shrink-0 space-x-1'
              )}
            >
              <Icon />
            </div>
          </div>
        ) : (
          children
        )}
      </button>
    );
  }
);

ToggleButton.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  name: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  type: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  role: PropTypes.string,
  children: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  textContainerClassName: PropTypes.string,
  showSelectedAfter: PropTypes.bool,
  refs: PropTypes.arrayOf(PropTypes.shape({ current: PropTypes.instanceOf(Element) })),
  setSelected: PropTypes.func,
  description: PropTypes.string,
};

export default ToggleButton;
